// AddGuestDrawer — "Add guest" per the design. A single-form Drawer (no tabs;
// guests have no Invite/Import variants) with the four identity fields the guest
// list carries. Submit → POST /guests. Editing a guest lives in GuestDetailDrawer.
// Validation mirrors the drawer convention (derived booleans + a `submitted`
// flag): first + last + at least one contact.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Drawer, Field, Icon, Input, PhoneInput } from '@/components/common';
import { countries, defaultCountry } from '@/mocks/countries';
import { useCreateGuestMutation } from '@/api/endpoints/peopleApi';

interface AddGuestDrawerProps {
  open: boolean;
  onClose: () => void;
}

const emptyDraft = {
  firstName: '',
  lastName: '',
  dialCode: defaultCountry.dial,
  mobileNumber: '',
  email: '',
};

const AddGuestDrawer = ({ open, onClose }: AddGuestDrawerProps) => {
  const { t } = useTranslation();
  const [createGuest, { isLoading: creating }] = useCreateGuestMutation();

  const [draft, setDraft] = useState(emptyDraft);
  const [submitted, setSubmitted] = useState(false);

  // Re-seed each time the drawer opens; discard leftovers on close.
  useEffect(() => {
    if (open) {
      setDraft(emptyDraft);
      setSubmitted(false);
    }
  }, [open]);

  const patch = (changes: Partial<typeof emptyDraft>) => setDraft((d) => ({ ...d, ...changes }));

  const firstMissing = !draft.firstName.trim();
  const lastMissing = !draft.lastName.trim();
  // Guests need a way to be reached — an email or a phone, either one.
  const contactMissing = !draft.email.trim() && !draft.mobileNumber.trim();

  const submit = async () => {
    setSubmitted(true);
    if (firstMissing || lastMissing || contactMissing) return;
    const hasPhone = !!draft.mobileNumber.trim();
    try {
      await createGuest({
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim(),
        email: draft.email.trim() || undefined,
        ...(hasPhone
          ? { countryCode: draft.dialCode, mobileNumber: draft.mobileNumber.trim() }
          : {}),
      }).unwrap();
      toast.success(t('add_guest_created'));
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      className='add-people-drawer'
      icon='user-plus'
      title={t('add_guest_title')}
      footer={
        <div className='add-people__footer'>
          <span className='add-people__required'>{t('add_guest_required_hint')}</span>
          <span className='add-people__footer-actions'>
            <Button variant='secondary' onClick={onClose} disabled={creating}>
              {t('button_cancel')}
            </Button>
            <Button iconStart='user-plus' onClick={submit} loading={creating}>
              {t('add_guest_submit')}
            </Button>
          </span>
        </div>
      }
    >
      <div className='add-people-drawer__body'>
        <div className='add-person'>
          <section className='add-person__card'>
            <p className='add-person__card-head'>
              <Icon name='user-round' size={16} />
              {t('add_person_section_personal')}
            </p>

            <div className='add-person__card-body'>
              <div className='add-person__row'>
                <Field
                  label={t('add_person_firstname')}
                  required
                  error={submitted && firstMissing ? t('add_person_err_firstname') : undefined}
                >
                  <Input
                    value={draft.firstName}
                    placeholder={t('add_person_firstname_ph')}
                    error={submitted && firstMissing}
                    onChange={(e) => patch({ firstName: e.target.value })}
                  />
                </Field>
                <Field
                  label={t('add_person_lastname')}
                  required
                  error={submitted && lastMissing ? t('add_person_err_lastname') : undefined}
                >
                  <Input
                    value={draft.lastName}
                    placeholder={t('add_person_lastname_ph')}
                    error={submitted && lastMissing}
                    onChange={(e) => patch({ lastName: e.target.value })}
                  />
                </Field>
              </div>
              <div className='add-person__row'>
                <Field label={t('add_person_phone')}>
                  <PhoneInput
                    countries={countries}
                    dialCode={draft.dialCode}
                    onDialCodeChange={(dial) => patch({ dialCode: dial })}
                    value={draft.mobileNumber}
                    onChange={(digits) => patch({ mobileNumber: digits })}
                    placeholder={t('add_person_phone_ph')}
                    error={submitted && contactMissing}
                    hideBadge
                  />
                </Field>
                <Field label={t('guest_label_email')}>
                  <Input
                    type='email'
                    iconStart='mail'
                    value={draft.email}
                    placeholder={t('add_person_email_ph')}
                    error={submitted && contactMissing}
                    onChange={(e) => patch({ email: e.target.value })}
                  />
                </Field>
              </div>
              {submitted && contactMissing && (
                <p className='add-person__error'>
                  <Icon name='circle-alert' size={14} />
                  {t('add_guest_err_contact')}
                </p>
              )}
            </div>
          </section>
        </div>
      </div>
    </Drawer>
  );
};

export default AddGuestDrawer;
